import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Tag,
    Avatar,
    notification,
    Popconfirm,
    Row,
    Col,
    Typography
} from 'antd';

import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    TeamOutlined
} from '@ant-design/icons';

import { channelPartnerService } from '../../services/channelPartnerService';
import ChannelPartnerForm from './ChannelPartnerForm';

const { Column } = Table;
const { Search } = Input;

const ChannelPartnerList = () => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);

    const fetchPartners = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await channelPartnerService.getAllChannelPartners({
                page,
                limit: pagination.pageSize,
                search
            });

            if (response?.success && response?.data) {
                setPartners(Array.isArray(response.data) ? response.data : []);

                setPagination(prev => ({
                    ...prev,
                    current: response?.pagination?.page || 1,
                    total: response?.pagination?.total || 0
                }));
            } else {
                setPartners([]);
            }
        } catch (error) {
            console.error('Error fetching channel partners:', error);
            notification.error({
                message: 'Error',
                description: error?.error || error?.message || 'Failed to fetch channel partners'
            });
            setPartners([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    const handleTableChange = (pagination) => {
        fetchPartners(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchPartners(1, value);
    };

    const handleAdd = () => {
        setSelectedPartner(null);
        setIsDrawerVisible(true);
    };

    const handleEdit = (partner) => {
        setSelectedPartner(partner);
        setIsDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await channelPartnerService.deleteChannelPartner(id);
            notification.success({
                message: 'Success',
                description: 'Channel Partner deleted successfully'
            });
            fetchPartners(pagination.current, searchText);
        } catch (error) {
            console.error('Delete channel partner error:', error);
            notification.error({
                message: 'Delete Failed',
                description: error?.response?.data?.error || error?.message || 'Failed to delete channel partner.'
            });
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedPartner(null);
    };

    const handleFormSubmit = () => {
        handleDrawerClose();
        fetchPartners(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[20, 20]}>
                {[
                    { title: 'Total Partners', value: pagination.total || 0, icon: <TeamOutlined />, color: '#1890ff', bg: '#e6f7ff' },
                ].map((item, index) => (
                    <Col xs={24} sm={8} key={index}>
                        <Card bordered={false} style={{
                            borderRadius: 16,
                            background: item.bg,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <div style={{ position: 'absolute', right: -10, top: -10, opacity: 0.1, fontSize: 60, color: item.color }}>
                                {item.icon}
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <Typography.Text type="secondary" style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                                    {item.title}
                                </Typography.Text>
                                <div style={{ marginTop: 8 }}>
                                    <Typography.Text style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                                        {item.value}
                                    </Typography.Text>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Card
                title="Channel Partners Management"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        size="large"
                    >
                        New Partner
                    </Button>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Search
                        placeholder="Search partners by name, mobile, email..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        onSearch={handleSearch}
                        style={{ width: '100%', maxWidth: 500 }}
                    />
                </div>
                <Table
                    dataSource={partners}
                    loading={loading}
                    rowKey="id"
                    onChange={handleTableChange}
                    pagination={pagination}
                    scroll={{ x: 1000 }}
                >
                    <Column
                        title="Partner"
                        key="name"
                        fixed="left"
                        render={(record) => (
                            <Space>
                                <Avatar
                                    size="large"
                                    style={{ backgroundColor: '#1890ff' }}
                                    icon={<TeamOutlined />}
                                />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{record.partnerName}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {record.partnerEmail}
                                    </div>
                                </div>
                            </Space>
                        )}
                    />

                    <Column
                        title="Mobile"
                        dataIndex="partnerMobile"
                        key="partnerMobile"
                    />

                    <Column
                        title="GSTN"
                        dataIndex="gstn"
                        key="gstn"
                        render={(gstn) => gstn || <span style={{ color: '#ccc' }}>-</span>}
                    />

                    <Column
                        title="Contact"
                        key="contact"
                        render={(record) => (
                            <div>
                                {record.contactName && (
                                    <div style={{ fontSize: '12px' }}>{record.contactName}</div>
                                )}
                                {record.contactNumber && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>{record.contactNumber}</div>
                                )}
                            </div>
                        )}
                    />

                    <Column
                        title="Bank Details"
                        key="bankDetails"
                        render={(record) => (
                            <div>
                                {record.bankName && (
                                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{record.bankName}</div>
                                )}
                                {record.accountNumber && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>A/c: {record.accountNumber}</div>
                                )}
                                {!record.bankName && !record.accountNumber && (
                                    <span style={{ color: '#ccc' }}>-</span>
                                )}
                            </div>
                        )}
                    />


                    <Column
                        title="Actions"
                        key="actions"
                        fixed="right"
                        render={(record) => (
                            <Space>
                                <Button
                                    type="text"
                                    icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                    onClick={() => handleEdit(record)}
                                />
                                <Popconfirm
                                    title="Delete Channel Partner"
                                    description="Are you sure you want to delete this partner?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Popconfirm>
                            </Space>
                        )}
                    />
                </Table>
            </Card>

            <Drawer
                title={selectedPartner ? 'Edit Channel Partner' : 'New Channel Partner'}
                width={800}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
                destroyOnClose
            >
                <ChannelPartnerForm
                    partner={selectedPartner}
                    onSuccess={handleFormSubmit}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default ChannelPartnerList;
